import type { LandingPage, LandingPageBody, PageFaq, PageSection } from "./landingPages";

const CTA_EN = {
  ctaHeading: "Plan it in Nellove — free forever",
  ctaBody:
    "Nellove is free forever for couples, wedding planners and agencies. Leave your email to request an early-access invitation; access is not immediate.",
  ctaButton: "Request an invitation",
  relatedHeading: "Keep reading",
  faqHeading: "Common questions",
};

const CTA_FR = {
  ctaHeading: "Organisez-le dans Nellove — gratuit pour toujours",
  ctaBody:
    "Nellove est gratuit pour toujours, pour les couples, les wedding planners et les agences. Laissez votre e-mail pour demander une invitation d'accès anticipé ; l'accès n'est pas immédiat.",
  ctaButton: "Demander une invitation",
  relatedHeading: "À lire ensuite",
  faqHeading: "Questions fréquentes",
};

const TIMELINE_EN: PageSection = {
  heading: "A planning timeline that works here",
  list: [
    "12 months or more: agree budget, guest count and date; book the venue.",
    "9–10 months: book the providers with the least availability — usually photography, catering and music.",
    "6–8 months: send save-the-dates, especially to guests who travel.",
    "3–4 months: finish the legal paperwork steps you can do in advance; confirm what the registering authority needs.",
    "6–8 weeks: send invitations with a clear RSVP deadline.",
    "Final 2 weeks: final head count to the caterer, day-of timeline shared with providers.",
  ],
  paragraphs: [
    "Nellove's free checklist builds this order from your real date, and shortens it when your date is close.",
  ],
};

const TIMELINE_FR: PageSection = {
  heading: "Un rétroplanning adapté",
  list: [
    "12 mois ou plus : budget, nombre d'invités et date ; réservation du lieu.",
    "9–10 mois : réservez les prestataires les plus demandés — en général photographe, traiteur et musique.",
    "6–8 mois : envoyez les faire-part « save the date », surtout aux invités qui voyagent.",
    "3–4 mois : lancez les démarches administratives possibles à l'avance ; vérifiez les pièces demandées.",
    "6–8 semaines : envoyez les invitations avec une date limite de réponse claire.",
    "2 dernières semaines : nombre final au traiteur, déroulé du jour J partagé aux prestataires.",
  ],
  paragraphs: [
    "La checklist gratuite de Nellove calcule cet ordre à partir de votre vraie date, et le resserre si la date est proche.",
  ],
};

const HOW_EN = (country: string): PageSection => ({
  heading: `How Nellove helps for a wedding in ${country}`,
  list: [
    `A country pack for ${country}: the legal stage in your plan is specific to it, not generic.`,
    "Guests and a unique RSVP link per guest, so replies land in one list.",
    "A budget ledger with planned, quoted, paid and due amounts.",
    "A day-of timeline you can share with providers.",
    "English and French interface. Free forever.",
  ],
});

const HOW_FR = (country: string): PageSection => ({
  heading: `Comment Nellove vous aide pour un mariage ${country}`,
  list: [
    "Un pack pays : l'étape légale de votre plan est propre à ce pays, pas générique.",
    "Les invités et un lien RSVP unique par invité, pour que les réponses arrivent dans une seule liste.",
    "Un budget avec montants prévus, devis, payés et à payer.",
    "Un déroulé du jour J à partager aux prestataires.",
    "Interface en français et en anglais. Gratuit pour toujours.",
  ],
});

const FAQ_COMMON_EN: PageFaq[] = [
  {
    question: "Is Nellove free?",
    answer: "Yes. Nellove is free forever, with no guest-count limit on the free offer.",
  },
  {
    question: "Does Nellove give legal advice?",
    answer:
      "No. It organises the steps and links to the official authority. Requirements change, and only the authority that registers your marriage is authoritative.",
  },
];

const FAQ_COMMON_FR: PageFaq[] = [
  {
    question: "Nellove est-il gratuit ?",
    answer: "Oui. Nellove est gratuit pour toujours, sans limite d'invités dans l'offre gratuite.",
  },
  {
    question: "Nellove donne-t-il des conseils juridiques ?",
    answer:
      "Non. Il organise les étapes et renvoie à l'autorité officielle. Les règles évoluent, et seule l'autorité qui enregistre votre mariage fait foi.",
  },
];

type Spec = {
  id: string;
  path: { en: string; fr: string };
  en: Omit<LandingPageBody, keyof typeof CTA_EN | "faq"> & { faq: PageFaq[]; country: string };
  fr: Omit<LandingPageBody, keyof typeof CTA_FR | "faq"> & { faq: PageFaq[]; country: string };
};

const build = (s: Spec): LandingPage => {
  const { country: cEn, ...en } = s.en;
  const { country: cFr, ...fr } = s.fr;
  return {
    id: s.id,
    path: s.path,
    content: {
      en: { ...CTA_EN, ...en, sections: [...en.sections, TIMELINE_EN, HOW_EN(cEn)], faq: [...en.faq, ...FAQ_COMMON_EN] },
      fr: { ...CTA_FR, ...fr, sections: [...fr.sections, TIMELINE_FR, HOW_FR(cFr)], faq: [...fr.faq, ...FAQ_COMMON_FR] },
    },
  };
};

export const COUNTRY_PAGES: LandingPage[] = [
  build({
    id: "country-canada",
    path: { en: "wedding-planning-canada", fr: "organiser-mariage-canada" },
    en: {
      country: "Canada",
      metaTitle: "Wedding planning in Canada — free forever wedding app, EN & FR | Nellove",
      metaDescription:
        "Plan a wedding in Canada: how provincial rules shape the legal steps, a planning timeline, and a free-forever bilingual wedding app for couples and planners.",
      eyebrow: "Canada",
      heading: "Planning a wedding in Canada",
      intro:
        "In Canada, marriage is administered by each province and territory, so the legal steps in Ontario are not the same as in Quebec or British Columbia. The logistics are the same everywhere — and Nellove handles both in English and French.",
      sections: [
        {
          heading: "The legal side depends on your province",
          paragraphs: [
            "Each province and territory sets who issues the licence or publication, which documents you need, and who may officiate. In Ontario, for example, marriage licences are issued by municipal offices — the Government of Ontario explains the process on its official page (ontario.ca/page/getting-married).",
            "Quebec has its own civil-law process for marriage. Check the requirements with the officiant and Quebec's civil registry before booking anything that depends on the date.",
          ],
        },
        {
          heading: "Bilingual weddings",
          paragraphs: [
            "Many Canadian weddings mix English and French guests. Nellove's interface works in both languages, so a couple and their planner can each work in the language they prefer.",
          ],
        },
      ],
      faq: [
        {
          question: "Is the legal process the same in every province?",
          answer: "No. Marriage is administered provincially, so check the rules of the province or territory where the ceremony takes place.",
        },
      ],
    },
    fr: {
      country: "au Canada",
      metaTitle: "Organiser un mariage au Canada — application gratuite pour toujours | Nellove",
      metaDescription:
        "Organiser un mariage au Canada et au Québec : les démarches selon la province, un rétroplanning et une application de mariage bilingue gratuite pour toujours.",
      eyebrow: "Canada",
      heading: "Organiser un mariage au Canada",
      intro:
        "Au Canada, le mariage relève de chaque province et territoire : les démarches en Ontario ne sont pas celles du Québec ou de la Colombie-Britannique. La logistique, elle, est la même partout — et Nellove gère les deux, en français et en anglais.",
      sections: [
        {
          heading: "Les démarches dépendent de votre province",
          paragraphs: [
            "Chaque province et territoire fixe qui délivre la licence ou la publication, les documents à fournir et qui peut célébrer. En Ontario, par exemple, les licences de mariage sont délivrées par les municipalités (ontario.ca/page/getting-married).",
            "Le Québec a sa propre procédure de droit civil. Vérifiez les exigences auprès du célébrant et de l'état civil du Québec avant de réserver ce qui dépend de la date.",
          ],
        },
        {
          heading: "Mariages bilingues",
          paragraphs: [
            "Beaucoup de mariages canadiens réunissent des invités francophones et anglophones. L'interface de Nellove fonctionne dans les deux langues, pour le couple comme pour l'organisateur.",
          ],
        },
      ],
      faq: [
        {
          question: "Les démarches sont-elles les mêmes dans toutes les provinces ?",
          answer: "Non. Le mariage relève des provinces : vérifiez les règles de la province ou du territoire où a lieu la cérémonie.",
        },
      ],
    },
  }),
  build({
    id: "country-france",
    path: { en: "wedding-planning-france", fr: "organiser-mariage-france" },
    en: {
      country: "France",
      metaTitle: "Getting married in France — steps, timeline and a free wedding app | Nellove",
      metaDescription:
        "Planning a wedding in France: the civil ceremony at the town hall, publication of banns, where you can marry, a timeline, and a free-forever wedding app.",
      eyebrow: "France",
      heading: "Planning a wedding in France",
      intro:
        "In France, the legal marriage is the civil ceremony at the mairie (town hall). A religious ceremony can follow, but it does not replace the civil one.",
      sections: [
        {
          heading: "Where you can marry",
          paragraphs: [
            "The marriage is celebrated in a commune with which one of you has a lasting link — for example the domicile or residence of one of you, or of one of your parents. The official conditions are on service-public.fr (Mariage civil, F930).",
          ],
        },
        {
          heading: "The file and the banns",
          paragraphs: [
            "You submit a marriage file to the mairie in advance, and the banns (publication des bans) are published before the ceremony. Ask the mairie early for its list of documents and its available dates — in popular months dates go quickly.",
          ],
        },
        {
          heading: "After the ceremony",
          paragraphs: ["The mairie gives you your livret de famille (family record book) after the civil ceremony."],
        },
      ],
      faq: [
        {
          question: "Is a religious ceremony enough in France?",
          answer: "No. The civil ceremony at the mairie is the legal marriage and must come first.",
        },
      ],
    },
    fr: {
      country: "en France",
      metaTitle: "Organiser son mariage en France — démarches, rétroplanning, appli gratuite | Nellove",
      metaDescription:
        "Organiser un mariage en France : mariage civil en mairie, publication des bans, commune du mariage, rétroplanning et application de mariage gratuite pour toujours.",
      eyebrow: "France",
      heading: "Organiser son mariage en France",
      intro:
        "En France, le mariage légal est la cérémonie civile en mairie. Une cérémonie religieuse peut suivre, mais elle ne la remplace pas.",
      sections: [
        {
          heading: "Dans quelle commune se marier",
          paragraphs: [
            "Le mariage est célébré dans une commune avec laquelle l'un de vous a un lien durable — par exemple le domicile ou la résidence de l'un de vous, ou de l'un de vos parents. Les conditions officielles sont sur service-public.fr (Mariage civil, F930).",
          ],
        },
        {
          heading: "Le dossier et la publication des bans",
          paragraphs: [
            "Vous déposez un dossier de mariage en mairie à l'avance, puis les bans sont publiés avant la cérémonie. Demandez tôt à la mairie la liste des pièces et ses dates disponibles : en haute saison, elles partent vite.",
          ],
        },
        {
          heading: "Après la cérémonie",
          paragraphs: ["La mairie vous remet le livret de famille après la cérémonie civile."],
        },
      ],
      faq: [
        {
          question: "Une cérémonie religieuse suffit-elle en France ?",
          answer: "Non. La cérémonie civile en mairie est le mariage légal et doit avoir lieu en premier.",
        },
      ],
    },
  }),
  build({
    id: "country-belgium",
    path: { en: "wedding-planning-belgium", fr: "organiser-mariage-belgique" },
    en: {
      country: "Belgium",
      metaTitle: "Getting married in Belgium — steps, timeline and a free wedding app | Nellove",
      metaDescription:
        "Planning a wedding in Belgium: the declaration of marriage at the commune, the civil ceremony, a planning timeline and a free-forever wedding app.",
      eyebrow: "Belgium",
      heading: "Planning a wedding in Belgium",
      intro:
        "In Belgium, the legal marriage is the civil ceremony before the registrar of the commune. It starts with a declaration of marriage at the commune; a religious ceremony can follow afterwards.",
      sections: [
        {
          heading: "Declaration of marriage first",
          paragraphs: [
            "You make a declaration of marriage at the commune, with the required documents, before the ceremony date can be fixed. The official overview is on belgium.be (Famille > Couple > Mariage). Ask the commune for its document list and timing before you book a venue.",
          ],
        },
        {
          heading: "Multilingual guest lists",
          paragraphs: [
            "Belgian weddings often bring together guests who speak different languages. Nellove works in English and French.",
          ],
        },
      ],
      faq: [
        {
          question: "Where do we start in Belgium?",
          answer: "With the declaration of marriage at the commune. Confirm the documents and timing with the commune directly.",
        },
      ],
    },
    fr: {
      country: "en Belgique",
      metaTitle: "Organiser son mariage en Belgique — démarches et appli gratuite | Nellove",
      metaDescription:
        "Organiser un mariage en Belgique : déclaration de mariage à la commune, cérémonie civile, rétroplanning et application de mariage gratuite pour toujours.",
      eyebrow: "Belgique",
      heading: "Organiser son mariage en Belgique",
      intro:
        "En Belgique, le mariage légal est la cérémonie civile devant l'officier de l'état civil de la commune. Tout commence par une déclaration de mariage ; une cérémonie religieuse peut suivre.",
      sections: [
        {
          heading: "D'abord la déclaration de mariage",
          paragraphs: [
            "Vous faites une déclaration de mariage à la commune, avec les documents requis, avant de pouvoir fixer la date. La présentation officielle est sur belgium.be (Famille > Couple > Mariage). Demandez à la commune la liste des pièces et les délais avant de réserver un lieu.",
          ],
        },
        {
          heading: "Des invités de plusieurs langues",
          paragraphs: ["Les mariages belges réunissent souvent des invités de langues différentes. Nellove fonctionne en français et en anglais."],
        },
      ],
      faq: [
        {
          question: "Par où commencer en Belgique ?",
          answer: "Par la déclaration de mariage à la commune. Confirmez les pièces et les délais directement avec elle.",
        },
      ],
    },
  }),
  build({
    id: "country-uk",
    path: { en: "wedding-planning-uk", fr: "organiser-mariage-royaume-uni" },
    en: {
      country: "the UK",
      metaTitle: "Wedding planning in the UK — notice, timeline and a free wedding app | Nellove",
      metaDescription:
        "Planning a wedding in the UK: giving notice at least 29 days before in England and Wales, Scotland's separate process, a timeline and a free-forever wedding app.",
      eyebrow: "United Kingdom",
      heading: "Planning a wedding in the UK",
      intro:
        "The UK has separate systems: England and Wales, Scotland, and Northern Ireland each have their own marriage rules. The first legal step is usually giving notice.",
      sections: [
        {
          heading: "England and Wales: give notice at least 29 days before",
          paragraphs: [
            "GOV.UK states that you must give notice at your local register office at least 29 days before your ceremony (gov.uk/marriages-civil-partnerships). Book your notice appointment early — it is often the step couples leave too late.",
          ],
        },
        {
          heading: "Scotland and Northern Ireland",
          paragraphs: [
            "Scotland has its own process, explained on mygov.scot (Getting married). Northern Ireland also has its own rules. Check the official source for the nation where the ceremony will take place.",
          ],
        },
      ],
      faq: [
        {
          question: "How long before the wedding must we give notice in England?",
          answer: "At least 29 days before the ceremony, according to GOV.UK.",
        },
      ],
    },
    fr: {
      country: "au Royaume-Uni",
      metaTitle: "Se marier au Royaume-Uni — démarches et appli gratuite | Nellove",
      metaDescription:
        "Organiser un mariage au Royaume-Uni : avis de mariage au moins 29 jours avant en Angleterre et au pays de Galles, règles propres à l'Écosse, rétroplanning.",
      eyebrow: "Royaume-Uni",
      heading: "Se marier au Royaume-Uni",
      intro:
        "Le Royaume-Uni a plusieurs systèmes : l'Angleterre et le pays de Galles, l'Écosse et l'Irlande du Nord ont chacun leurs règles. La première étape légale est en général l'avis de mariage (giving notice).",
      sections: [
        {
          heading: "Angleterre et pays de Galles : avis au moins 29 jours avant",
          paragraphs: [
            "Selon GOV.UK, l'avis doit être donné au bureau d'état civil local au moins 29 jours avant la cérémonie (gov.uk/marriages-civil-partnerships). Réservez ce rendez-vous tôt.",
          ],
        },
        {
          heading: "Écosse et Irlande du Nord",
          paragraphs: [
            "L'Écosse a sa propre procédure (mygov.scot, Getting married), tout comme l'Irlande du Nord. Vérifiez la source officielle de la nation où a lieu la cérémonie.",
          ],
        },
      ],
      faq: [
        {
          question: "Combien de temps avant faut-il donner l'avis en Angleterre ?",
          answer: "Au moins 29 jours avant la cérémonie, selon GOV.UK.",
        },
      ],
    },
  }),
  build({
    id: "country-usa",
    path: { en: "wedding-planning-usa", fr: "organiser-mariage-etats-unis" },
    en: {
      country: "the US",
      metaTitle: "Wedding planning in the USA — marriage license, timeline, free app | Nellove",
      metaDescription:
        "Planning a wedding in the United States: how marriage licenses work by state and county, a planning timeline and a free-forever wedding planning app.",
      eyebrow: "United States",
      heading: "Planning a wedding in the United States",
      intro:
        "In the US, marriage rules are set by each state, and the marriage license is usually issued at the county level. Waiting periods, how long a license stays valid and who may officiate all vary.",
      sections: [
        {
          heading: "The marriage license",
          paragraphs: [
            "Apply with the county clerk (or equivalent office) where the ceremony will take place, not necessarily where you live. Ask them three things: is there a waiting period, how long is the license valid, and who returns the signed license after the ceremony.",
          ],
        },
        {
          heading: "Marrying in another state",
          paragraphs: [
            "Destination weddings within the US are common. Check the rules of the state where you marry, and whether your officiant is allowed to officiate there.",
          ],
        },
      ],
      faq: [
        {
          question: "Where do we get a marriage license?",
          answer: "Usually from the county clerk's office (or equivalent) in the place where the ceremony happens. Rules vary by state.",
        },
      ],
    },
    fr: {
      country: "aux États-Unis",
      metaTitle: "Se marier aux États-Unis — licence de mariage et appli gratuite | Nellove",
      metaDescription:
        "Organiser un mariage aux États-Unis : la licence de mariage selon l'État et le comté, un rétroplanning et une application de mariage gratuite pour toujours.",
      eyebrow: "États-Unis",
      heading: "Se marier aux États-Unis",
      intro:
        "Aux États-Unis, les règles du mariage sont fixées par chaque État, et la licence est généralement délivrée par le comté. Délai d'attente, durée de validité et célébrants autorisés varient.",
      sections: [
        {
          heading: "La licence de mariage",
          paragraphs: [
            "Faites la demande auprès du county clerk (ou bureau équivalent) du lieu de la cérémonie. Posez trois questions : y a-t-il un délai d'attente, combien de temps la licence est-elle valable, et qui renvoie la licence signée après la cérémonie.",
          ],
        },
        {
          heading: "Se marier dans un autre État",
          paragraphs: ["Vérifiez les règles de l'État où vous vous mariez, et si votre célébrant a le droit d'y officier."],
        },
      ],
      faq: [
        {
          question: "Où obtenir une licence de mariage ?",
          answer: "En général auprès du county clerk (ou équivalent) du lieu de la cérémonie. Les règles varient selon l'État.",
        },
      ],
    },
  }),
];
